import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { Entity, MvpQueryResponseDto, AuthorizedCounts, UnauthorizedLicense } from "../models/mvp-query-response-dto";
import { BehaviorSubject, tap, EMPTY, Subject, throwError } from "rxjs";
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { catchError, finalize, map, takeUntil } from 'rxjs/operators';
import { EntityType } from "../enums/entity-type.enum";
import { LinkInfoDto } from "../models/link-info-dto";
import { EntityTypeService } from "./entity-type.service";
import { SearchFieldsService } from "./search-fields.service";
import { ApiService } from "./api.service";
import { FieldsListsService } from "./fields-lists.service";
import { FieldsListsDto } from "../models/fields-lists-dto";
import { SearchFieldsDto } from "../models/search-fields-dto";
import { SearchFieldDto, ThesaurusSearchType } from "../models/search-field-dto";
import { AdvancedThesaurusItem } from "../models/advanced-thesaurus-item";
import { DEFAULT_ENTITY_INDEX, ENTITY_INDEX, ENTITY_TYPE_NAMES, ENTITY_TYPE_TO_TAB_ID } from "../constants";


import { MultiLangStrDto } from "../models/multi-lang-str-dto";
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GaService } from "./ga.service";
import { DialogLauncherService } from "./dialog-launcher.service";
import { ResultsMessageComponent } from "../components/results-message/results-message.component";
import { LanguageService } from "./language.service";
import { TranslateLangPipe } from "../components/shared/pipes/translateLang.pipe";
import PopupTemplate from '@arcgis/core/PopupTemplate';
import { TranslateService } from "@ngx-translate/core";
import { ShapeItem } from "../components/arcgis-map/constants/shape-item.interface";


const entitiesWithLink: string[] = [EntityType.LOCUS, EntityType.OBJECT, EntityType.BASKET, EntityType.EXCAVATION, EntityType.SURVEY, EntityType.HACHRAZA, EntityType.SCIENTIFIC_ARCHIVE_DOCUMENTS, EntityType.SCIENTIFIC_ARCHIVE_FOLDERS, EntityType.FRAGMENT, EntityType.MANUSCRIPT, EntityType.PHOTOGRAMMETRIC, EntityType.ORTHOPHOTO, EntityType.PLASTER_SAMPLE, EntityType.CONSERVATION_ARCHIVE_DOCUMENTS, EntityType.CONSERVATION_ARCHIVE_FOLDERS, EntityType.HB_SECTION, EntityType.COIN ];

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  searchFields: SearchFieldsDto = {} as SearchFieldsDto;
  fieldsLists: FieldsListsDto = {} as FieldsListsDto;
  currentLanguage!: string;
  hasShapes: boolean = false;

  private unfilteredSearchResultsSubject: BehaviorSubject<Entity[]> = new BehaviorSubject<Entity[]>([]);
  private searchResultsSubject: BehaviorSubject<Entity[]> = new BehaviorSubject<Entity[]>([]);
  searchResults$ = this.searchResultsSubject.asObservable();
  private shapesSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  shapes$ = this.shapesSubject.asObservable();
  private authorizedCountsSubject: BehaviorSubject<AuthorizedCounts> = new BehaviorSubject<AuthorizedCounts>({} as AuthorizedCounts);
  private unauthorizedCountsSubject: BehaviorSubject<AuthorizedCounts> = new BehaviorSubject<AuthorizedCounts>({} as AuthorizedCounts);
  authorizedCounts$ = this.authorizedCountsSubject.asObservable();
  unauthorizedCounts$ = this.unauthorizedCountsSubject.asObservable();
  private unauthorizedLicencesSubject: BehaviorSubject<UnauthorizedLicense[]> = new BehaviorSubject<UnauthorizedLicense[]>([] as UnauthorizedLicense[]);
  unauthorizedLicenses$ = this.unauthorizedLicencesSubject.asObservable();

  private selectedShapeIdSubject: BehaviorSubject<number> = new BehaviorSubject<number>(-1);
  selectedShapeId$ = this.selectedShapeIdSubject.asObservable();
  private isLoadingResultsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoadingResults$ = this.isLoadingResultsSubject.asObservable();
  private cancelSearchSubject: Subject<void> = new Subject<void>();
  private isFirstSearchResReturnedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isFirstSearchResReturned$ = this.isFirstSearchResReturnedSubject.asObservable();
  private searchClearedSubject: Subject<void> = new Subject<void>();
  searchCleared$ = this.searchClearedSubject.asObservable();
  private isErrorTooManyRowsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isErrorTooManyRows$ = this.isErrorTooManyRowsSubject.asObservable();
  private isErrorNoPolygonsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isErrorNoPolygons$ = this.isErrorNoPolygonsSubject.asObservable();
  private isResultsPopupOpenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isResultsPopupOpen$ = this.isResultsPopupOpenSubject.asObservable();
  private withThesaurusAncestorsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  withThesaurusAncestors$ = this.withThesaurusAncestorsSubject.asObservable();
  private withEntityAncestorsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  withEntityAncestors$ = this.withEntityAncestorsSubject.asObservable();
  private withImagesOnlySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  withImagesOnly$ = this.withImagesOnlySubject.asObservable();
  isSkipAutoSearch = false;


  constructor(private translateLang: TranslateLangPipe
,    private apiService: ApiService, private router: Router, private route: ActivatedRoute, private gaService: GaService,
    private entityTypeService: EntityTypeService, private fieldsListsService: FieldsListsService,
    private searchFieldsService: SearchFieldsService, private dialogLauncherService: DialogLauncherService,
    private languageService: LanguageService,
    private translate: TranslateService
  ) {
    this.fieldsListsService.fieldsLists$.subscribe((fieldsLists) => {
      this.fieldsLists = fieldsLists;
    });
    this.searchFieldsService.searchFields$.subscribe((searchFields) => {
      this.searchFields = searchFields;
    });
    this.languageService.currentLanguage$.subscribe((currentLanguage) => {
      this.currentLanguage = currentLanguage;
      // Reprocess shapes when language changes to update layer names
      this.reprocessShapesForLanguageChange();
    });
    this.searchFieldsService.clearSearchFields();
  }

  isDiffTabId(params: ParamMap, previousParams: ParamMap) {
    if (params.get('tabId') !== previousParams.get('tabId')) {
      return true;
    }
    return false;
  }

  initSearchParams(params: ParamMap, previousParams: ParamMap, shouldSkipSearch: boolean = false): void {
    if (!this.isSkipAutoSearch) {
      const request = this.buildRequest(params);
      if (!previousParams ||
        this.searchFieldsService.isDiffParamsSearchFields(params, previousParams)
        || (!!params && this.searchResultsSubject.value.length === 0 && !this.isDiffTabId(params, previousParams))) {
        this.searchFieldsService.clearSearchFields();
        this.clearSearchResults();
        this.searchFieldsService.populateSearchFields(request);
        !shouldSkipSearch && this.search().subscribe();
      }
    } else {
      this.isSkipAutoSearch = false;
    }
  }



  setSearchParams() {
    const queryParams: any = Object.entries(this.searchFields)
      .map(([key, value]) => {
        // For advancedThesaurus, encode as categoryId:address:searchType tuples
        if (key === 'advancedThesaurus') {
          const encodedValues = value?.map((field: SearchFieldDto) => {
            const searchType = field.searchType || 'branch';
            // Use address as the unique identifier in URL
            const address = field.id || '';
            return `${field.categoryId}:${address}:${searchType}`;
          }).join(',');
          return encodedValues ? { [key]: encodedValues } : null;
        }
        const ids = value?.map((field: SearchFieldDto) => field.id).join(',');
        return ids ? { [key]: ids } : null;
      })
      .filter(obj => obj !== null)
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
    
    queryParams[ENTITY_INDEX] = DEFAULT_ENTITY_INDEX;
    const filteredPrevQueryParams = this.getFilteredPrevSearchParams();
    this.isSkipAutoSearch = true;
    this.router.navigate([], { queryParams: { ...filteredPrevQueryParams, ...queryParams } });
  }

  getFilteredPrevSearchParams() {
    const currentQueryParams = this.route.snapshot.queryParams || {};
    const filteredQueryParams = Object.entries(currentQueryParams)
      .filter(([key]) => !this.searchFields.hasOwnProperty(key) || key === 'entityType' || key === 'entityId')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    return filteredQueryParams;
  }

  getLinkInfo(entity: any): LinkInfoDto {
    const isNewTab = entity.externalUrl !== null;
    if (isNewTab) {
      return new LinkInfoDto(isNewTab, entity.externalUrl, {})
    } else {
      // Normalize to the singular entity type (e.g. 'coins' -> 'coin') so navigation
      // matches what the entity page components listen for. No-op when already singular.
      return new LinkInfoDto(isNewTab, '', { entityId: entity.entityId, entityType: this.entityTypeService.getSingleEntityType(entity.entityType) });
    }
  }

private processResultsRes(response: MvpQueryResponseDto): MvpQueryResponseDto {
  const bucketImageEntityTypes = [EntityType.OBJECT, EntityType.SCIENTIFIC_ARCHIVE_DOCUMENTS, EntityType.SCIENTIFIC_ARCHIVE_FOLDERS, EntityType.CONSERVATION_ARCHIVE_DOCUMENTS, EntityType.CONSERVATION_ARCHIVE_FOLDERS, EntityType.PHOTOGRAMMETRIC, EntityType.LOCUS, EntityType.DANA_PDF, EntityType.PLASTER_SAMPLE, EntityType.HB_SECTION];
    return {
      ...response,
      entities: response.entities.map(entity => {
        const shouldAddLinkInfo = entity.externalUrl || entitiesWithLink.includes(this.entityTypeService.getSingleEntityType(entity.entityType));
        return {
          ...entity,
          ...(shouldAddLinkInfo ? { linkInfo: this.getLinkInfo(entity) } : {}),
          ...(entity.imageUrl && entity.entityType == EntityType.FRAGMENT ? {imageUrl: `${entity.imageUrl}/0,0,7216,5412/pct:5/0/default.jpg`}
            : entity.imageUrl && bucketImageEntityTypes.includes(entity.entityType as EntityType) ? {
                imageUrl: entity.imageUrl.includes('?') 
                  ? entity.imageUrl + '&type=thumbnail'
                  : entity.imageUrl + '?type=thumbnail'
              } : {}
          ),
          entityTypeName: ENTITY_TYPE_NAMES[entity.entityType] || entity.entityType
        };
      }),
    };
}

private prepareRequest(): any {
  const params: any = Object.fromEntries(
    Object.entries(this.searchFields).map(([key, fields]) => [
      key,
      fields.map((field: SearchFieldDto) => field.id)
    ])
  );
  
  // Merge expedition and excavation licenses into the main licenses array
  const expeditionLicenses = params['expeditionLicenses'] || [];
  const excavationLicenses = params['excavationLicenses'] || [];
  const surveyLicenses = params['surveyLicenses'] || [];
  const existingLicenses = params['licenses'] || [];
  params['licenses'] = [...existingLicenses, ...expeditionLicenses, ...excavationLicenses, ...surveyLicenses];
  
  const fieldsWithWids = ['periodMids', 'materialMids', 'findTypeMids'];
  const intFields = ['findTypeMids', 'regionIds', 'thesaurusWids', 'advancedThesaurus', 'licenses', 'expeditions', 'excavations', 'surveys', 'periodMids', 'materialMids', 'settlementIds', 'persons', 'hachrazot', 'heritageSiteIds'];
  const booleanFields = ['withThesaurusAncestors', 'withEntityAncestors'];
  const allFields = ['entityTypes', 'regions', 'freeText', 'scientificArchiveDocuments', 'scientificArchiveFolders', 'conservationArchiveDocuments', 'conservationArchiveFolders', ...intFields, ...booleanFields];

  const result: any = {};

  // Process all fields
  allFields.forEach(field => {
    const pascalCase = field.charAt(0).toUpperCase() + field.slice(1);
    if (booleanFields.includes(field)) {
      if (field === 'withThesaurusAncestors') {
        result[pascalCase] = this.withThesaurusAncestorsSubject.value;
      } else if (field === 'withEntityAncestors') {
        result[pascalCase] = this.withEntityAncestorsSubject.value;
      } else {
        result[pascalCase] = true;
      }
    } else {
      const value = params[field] || [];
      result[pascalCase] = intFields.includes(field) ? value.map(Number) : value;
    }
  });

  fieldsWithWids.forEach(field => {
    const fieldKey = field as keyof SearchFieldsDto;
    if (this.searchFields[fieldKey] && this.searchFields[fieldKey].length > 0) {
      const widsKey = field.replace('Mids', 'Wids');
      const finalWidsKey = widsKey.charAt(0).toUpperCase() + widsKey.slice(1);
      const wids = this.searchFields[fieldKey]
        .map((f: SearchFieldDto) => {
          return f.wid;
        })
        .filter((wid: string | undefined) => wid !== undefined)
        .map(Number);
      result[finalWidsKey] = wids;
      
      const addressesKey = field.replace('Mids', 'Addresses');
      const finalAddressesKey = addressesKey.charAt(0).toUpperCase() + addressesKey.slice(1);
      const addresses = this.searchFields[fieldKey]
        .map((f: SearchFieldDto) => f.address)
        .filter((address: string | undefined) => address !== undefined && address !== '');
      result[finalAddressesKey] = addresses;
    }
  });

  // Handle advancedThesaurus separately - group by categoryId with new structure
  // Always initialize as empty dictionary (not array)
  const advancedThesaurus: { [categoryId: number]: AdvancedThesaurusItem[] } = {};

  if (this.searchFields.advancedThesaurus && this.searchFields.advancedThesaurus.length > 0) {
    this.searchFields.advancedThesaurus.forEach((f: SearchFieldDto) => {
      const catId = f.categoryId!;
      
      // Initialize array for this category if needed
      if (!advancedThesaurus[catId]) {
        advancedThesaurus[catId] = [];
      }

      // Get the search type (default to 'branch' if not specified)
      const searchType = f.searchType || 'branch';
      const backendMode = searchType;

      // Create the item based on mode
      const item: AdvancedThesaurusItem = { mode: backendMode };

      switch (backendMode) {
        case 'branch':
          if (f.address) {
            item.address = f.address;
          }
          break;
        case 'term':
          // f.id is the address, need to extract MID from stored node info
          if (f.mid !== undefined) {
            item.mid = f.mid;
          }
          break;
        case 'word':
          if (f.wid) {
            const wid = Number(f.wid);
            if (!isNaN(wid)) {
              item.wid = wid;
            }
          }
          break;
      }

      advancedThesaurus[catId].push(item);
    });
  }

  // Always set the dictionary (even if empty)
  result.AdvancedThesaurus = advancedThesaurus;

  return result;
}

  getEntityIndex(entityId: number): number {
    return this.searchResultsSubject.value.findIndex((entity: Entity) => entity.entityId === entityId);
  }
  createShapesPopup(layerName: string, shapeData: any): PopupTemplate {
    const entityType = shapeData?.entity?.entityType;
    const isOrthophoto = entityType === 'orthophoto' || entityType === 'orthophotos';
    return new PopupTemplate({
      title: (feature: any) => {
        const entity = feature.graphic?.attributes?.shape?.shapeData?.entity;
        if (!entity) return '';
        let translated_title = '';
        if (typeof entity.title === 'string') {
          translated_title = entity.title;
        } else if (entity.title && typeof entity.title === 'object') {
          translated_title = this.currentLanguage === 'he' 
            ? (entity.title.hebrew || entity.title.english || '')
            : (entity.title.english || entity.title.hebrew || '');
        }
        return `<div id="popup" style="justify-content:right !important;">${translated_title}</div>`;
      },
      content: (feature: any) => {
        const entity = feature.graphic?.attributes?.shape?.shapeData?.entity;
        if (!entity) return '<div id="popup">No data available</div>';
        let translated_description = '';
        if (typeof entity.description === 'string') {
          translated_description = entity.description;
        } else if (entity.description && typeof entity.description === 'object') {
          translated_description = this.currentLanguage === 'he'
            ? (entity.description.hebrew || entity.description.english || '')
            : (entity.description.english || entity.description.hebrew || '');
        }
        return `
        <div id="popup">
          ${translated_description
            ? `<strong>${translated_description}</strong><br/>` 
            : ``}
          ${entity.imageUrl && entity.entityType !== 'orthophotos'
            ? `<img src="${entity.imageUrl}" alt="" 
                    style="max-width: 100%; height: auto; display: block; margin-top: 10px;" 
                    onerror="this.style.display='none'"/>`
            : ``}
        </div>
      `;
      },
      actions: isOrthophoto ? [] : [
        {
          id: 'show-details',
          title: this.translate.instant('iaa.map.viewDetails'),
          className: 'esri-icon-description',
          type: "button"
        }
      ]
    });
  }

  countEntityTypes(entities: Entity[]): any {
    const entityTypeCount: any = {};
    
    entities.forEach(entity => {
      // Get the translated entity type name based on current language - ensure we get a string
      let entityTypeName = '';
      if (typeof entity.entityTypeName === 'string') {
        entityTypeName = entity.entityTypeName;
      } else if (entity.entityTypeName && typeof entity.entityTypeName === 'object') {
        entityTypeName = this.currentLanguage === 'he' 
          ? (entity.entityTypeName.hebrew || entity.entityTypeName.english || '')
          : (entity.entityTypeName.english || entity.entityTypeName.hebrew || '');
      }
      
      if (entityTypeCount.hasOwnProperty(entityTypeName)) {
        entityTypeCount[entityTypeName]++;
      } else {
        entityTypeCount[entityTypeName] = 1;
      }
    });

    return entityTypeCount;
  }

  createRedundantShapesPopup(shapeData: any): PopupTemplate {
    const entityTypeCount = this.countEntityTypes(shapeData.entities);
    let totalCount: number = 0;
    for (let count of Object.values(entityTypeCount)) {
      totalCount += Number(count);
    }
    let space = " ";
    
    // Get the translated entity type name based on current language - ensure we get a string
    let translatedEntityTypeName = '';
    if (typeof shapeData.entity.entityTypeName === 'string') {
      translatedEntityTypeName = shapeData.entity.entityTypeName;
    } else if (shapeData.entity.entityTypeName && typeof shapeData.entity.entityTypeName === 'object') {
      translatedEntityTypeName = this.currentLanguage === 'he' 
        ? (shapeData.entity.entityTypeName.hebrew || shapeData.entity.entityTypeName.english || '')
        : (shapeData.entity.entityTypeName.english || shapeData.entity.entityTypeName.hebrew || '');
    }
    
    const resultsText = this.currentLanguage === 'he' ? 'תוצאות' : 'results';
    const primaryEntityType = shapeData?.entity?.entityType;
    const isOrthophoto = primaryEntityType === 'orthophoto' || primaryEntityType === 'orthophotos';

    return new PopupTemplate({
      title: `<div id="popup" dir="rtl">${translatedEntityTypeName}${space}-${space}${totalCount}${space}${resultsText} </div>`,
      content: [{
        type: "text",
        text: `<div id="popup"><h4></h4></br>${Object.entries(entityTypeCount)
          .map(([entityType, count]) => `<p>${entityType}: ${count}${space}${resultsText} </p>`)
          .join('')}</div></br>`
      }],
      actions: isOrthophoto ? [] : [
      {
       id: 'show-details',
       title: this.translate.instant('iaa.map.viewDetails'),
       className: 'esri-icon-description',
        type: "button"
      }]
    });
  }

  private processShapes(entities: Entity[]): ShapeItem[] {
    const shapesList: any[] = [];
    const processedShapesList: ShapeItem[] = [];    
    // First pass: Group entities by WKT to reduce the number of graphics
    entities.forEach(entity => {
      if (!entity.shapes || entity.shapes.length === 0) {
        return;
      }
            
      entity.shapes.forEach(shapeInfo => {
        const shapeWKT = shapeInfo.wkt;
        const shapeId = shapeInfo.shapeId;
        const existingShape = shapesList.find(existing => existing.wkt === shapeWKT);
        
        if (existingShape) {
          // Add entity to existing shape's entities array
          existingShape.shapeData.entities.push(entity);
        } else {
          // Create new shape entry
          const newShape = { 
            wkt: shapeWKT, 
            shapeData: { entity: entity, entities: [entity] }, 
            shapeId: shapeId 
          };
          shapesList.push(newShape);
        }
      });
    });
    
    // Create shape items - multiple graphics for navigation when ≤30 entities
    shapesList.forEach(shape => {
      const allEntities = shape.shapeData.entities;
      const primaryEntity = allEntities[0];
      const isLessThanXEntities = allEntities.length <= 30;

      if (isLessThanXEntities) {
        allEntities.forEach((entity: Entity) => {
          processedShapesList.push({
            shapeId: `${shape.shapeId}-${entity.entityId}`,
            wkt: shape.wkt,
            layerName: this.translate.instant('iaa.map.queryResultsLayer'),
            hidden: false,
            shapeData: { entity: entity, entities: [entity] },
            popupTemplate: this.createShapesPopup(this.translate.instant('iaa.map.queryResultsLayer'), { entity: entity }),
            styledParams: {
              fillColor: [255, 180, 238, 0.2],
              borderColor: [207, 100, 193, 0.9],
              borderWidth: 2,
            }
          });
        });
      } else {
        processedShapesList.push({
          shapeId: shape.shapeId.toString(),
          wkt: shape.wkt,
          layerName: this.translate.instant('iaa.map.queryResultsLayer'),
          hidden: false,
          shapeData: { entity: primaryEntity, entities: allEntities },
          popupTemplate: this.createRedundantShapesPopup({ entities: allEntities, entity: primaryEntity }),
          styledParams: {
            fillColor: [255, 180, 238, 0.2],
            borderColor: [207, 100, 193, 0.9],
            borderWidth: 2,
          }
        });
      }
    });
    
    return processedShapesList;
  }

  private filterAuthorizedCounts(authorizedCounts: { [key: string]: number }): { [key: string]: number } {
    return Object.keys(authorizedCounts)
      .reduce((obj: any, key) => {
        obj[key] = authorizedCounts[key];
        return obj;
      }, {});
  }
  search(): Observable<MvpQueryResponseDto> {
    const request = this.prepareRequest();
    if (!this.shouldTriggerSearch(request)) {
      // this.openDialogWelcome();
      return EMPTY;
    }
    this.isLoadingResultsSubject.next(true);
    return this.apiService.searchEntities(request).pipe(
      map((response: MvpQueryResponseDto) => {
        if (response.isPartialResult === true) {
          this.showPartialResultsWarning();
        }
        return this.processResultsRes(response);
      }),
      tap((response) => {
        this.unfilteredSearchResultsSubject.next(response.entities);
        this.applyImageFilter();
        
        // Update stats
        const filteredAuthorizedCounts = this.filterAuthorizedCounts(response.stats.authorizedCounts);
        const filteredUnauthorizedCounts = this.filterAuthorizedCounts(response.stats.unauthorizedCounts);
        this.authorizedCountsSubject.next(filteredAuthorizedCounts);
        this.unauthorizedCountsSubject.next(filteredUnauthorizedCounts);
        
        if (response.unauthorizedLicensesAndExpeditions?.length > 0) {
          this.unauthorizedLicencesSubject.next(response.unauthorizedLicensesAndExpeditions);
        }
        
        this.shapesSubject.next(this.processShapes(response.entities));
        this.hasShapes = response.entities.some((entity: Entity) => entity.shapes !== null);
        
        if (response.entities.length === 0) {
          this.openDialogNoResultsFound();
        }
        
        this.isErrorTooManyRowsSubject.next(false);
        this.isErrorNoPolygonsSubject.next(false);
        const labelMap: { [key: string]: string[] } = {};
        Object.entries(this.searchFields).forEach(
          ([key, fields]: [string, SearchFieldDto[]]) => {
            const labels: string[] = fields
              .map<string>((f: SearchFieldDto) => {
                if (typeof f.label === 'string') {
                  return f.label;
                }
                const multi = f.label as MultiLangStrDto;
                if (this.currentLanguage === 'he' && multi.hebrew) return multi.hebrew;
                if (this.currentLanguage !== 'he' && multi.english) return multi.english;
                return multi.hebrew || multi.english || '';
              })
              .filter((lbl: string) => lbl !== '');
            if (labels.length) {
              labelMap[key] = labels;
            }
          }
        );
        const labelParams = Object.entries(labelMap)
        .map(
          ([key, names]) =>
            `${key}=${names.join(',')}`
        )
        .join('&');
        const authType = localStorage.getItem('authType') || 'unknown';
        this.gaService.trackWithGA(labelParams, 'searchParams', authType);
      }),
      catchError((error) => {
        if (error.error?.code === 'NO_POLYGON') {
          this.isErrorNoPolygonsSubject.next(true);
          const data = {
            title: 'iaa.app.results.NoPolygons',
            imgSrc: 'assets/too-many-results.svg',
            imgAlt: 'No Polygons',
            directionText: this.currentLanguage === 'he' ? 'rtl' : 'ltr'
          };
          this.openErrorDialog(data);
        }
        this.isLoadingResultsSubject.next(false);
        return throwError(() => error);
      }),
      finalize(() => {
        this.isFirstSearchResReturnedSubject.next(true);
        this.isLoadingResultsSubject.next(false);
      }),
      takeUntil(this.cancelSearchSubject)
    );
  }

  openErrorDialog(data?: any) {
    this.dialogLauncherService.openDialog(ResultsMessageComponent, data, '25vw', '28vw');
  }

  showPartialResultsWarning() {
    const data = {
      title: 'iaa.app.results.partialResults',
      imgSrc: 'assets/too-many-results.svg',
      imgAlt: 'Partial Results',
      directionText: this.currentLanguage === 'he' ? 'rtl' : 'ltr'
    };
    this.dialogLauncherService.openDialog(ResultsMessageComponent, data, '25vw', '28vw');
  }

  openDialogNoResultsFound() {
    this.updateIsResultsPopupOpen(true);
    const data = {
      title: 'iaa.app.results.noResultsForYou',
      imgSrc: 'assets/no-results-blue.svg',
      imgAlt: 'No Results Found',
      directionText: this.currentLanguage === 'he' ? 'rtl' : 'ltr'
    };
    this.dialogLauncherService.openDialog(ResultsMessageComponent, data, '25vw', '25vw');
  }

  openDialogWelcome() {
    const data = {
      title: 'iaa.general.welcome',
      imgSrc: 'assets/no-results-blue.svg',
      imgAlt: 'welcome',
      directionText: this.currentLanguage === 'he' ? 'rtl' : 'ltr'
    };
    this.dialogLauncherService.openDialog(ResultsMessageComponent, data, '30vw', '25vw');
  }

  clearSearchResults() {
    this.unfilteredSearchResultsSubject.next([]);
    this.searchResultsSubject.next([]);
    this.searchClearedSubject.next();
  }

  cancel() {
    this.cancelSearchSubject.next();
    this.selectedShapeIdSubject.next(-1);
    this.isLoadingResultsSubject.next(false);
  }

  private buildRequest(params: ParamMap): { [key: string]: any } {
    const request: { [key: string]: any } = {};
    
    params.keys.forEach(key => {
      if (key === 'regions') {
        const regionsValue = params.get(key);
        if (regionsValue) {
          request[key] = regionsValue.split(/(?=POLYGON)/);
        } else {
          request[key] = null;
        }
      } else if (key === 'advancedThesaurus') {
        // Parse categoryId:mid:searchType tuples and group by category
        const values = params.get(key)?.split(',') || [];
        const grouped: { [categoryId: string]: Array<{mid: string, searchType: string}> } = {};
        
        values.forEach(value => {
          const parts = value.split(':');
          const categoryId = parts[0];
          const mid = parts[1];
          const searchType = parts[2] || 'branch'; // Default to 'branch' if not specified
          
          if (categoryId && mid) {
            if (!grouped[categoryId]) {
              grouped[categoryId] = [];
            }
            grouped[categoryId].push({ mid, searchType });
          }
        });
        
        request['advancedThesaurus'] = Object.keys(grouped).length > 0 ? grouped : null;
      } else {
        request[key] = params.get(key)?.split(',') || null;
      }
    });
    
    return request;
  }

  private shouldTriggerSearch(request: { [key: string]: any }): boolean {
    const hasValues = Object.values(request).some(value => {
      if (!value) return false;
      
      // Handle arrays (most fields)
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      // Handle dictionaries (like advancedThesaurus, AdvancedThesaurusWids, AdvancedThesaurusAddresses)
      if (typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      
      return false;
    });
    
    return hasValues && request['skip_search']?.[0] !== '1';
  }

  updateSelectedShapeId(shapeId: number) {
    this.selectedShapeIdSubject.next(shapeId);
  }

  updateIsFirstResReturned(isFirstSearchResReturned: boolean) {
    this.isFirstSearchResReturnedSubject.next(isFirstSearchResReturned);
  }
  updateIsResultsPopupOpen(isResultsPopupOpen: boolean) {
    this.isResultsPopupOpenSubject.next(isResultsPopupOpen);
  }

  toggleWithThesaurusAncestors(value: boolean) {
    this.withThesaurusAncestorsSubject.next(value);
    this.retriggerSearchIfNeeded();
  }

  toggleWithEntityAncestors(value: boolean) {
    this.withEntityAncestorsSubject.next(value);
    this.retriggerSearchIfNeeded();
  }

  toggleWithImagesOnly(value: boolean) {
    this.withImagesOnlySubject.next(value);
    this.applyImageFilter();
  }

  private applyImageFilter() {
    const unfilteredResults = this.unfilteredSearchResultsSubject.value;
    const withImagesOnly = this.withImagesOnlySubject.value;
    
    if (withImagesOnly) {
      const filteredResults = unfilteredResults.filter(entity => entity.imageUrl);
      this.searchResultsSubject.next(filteredResults);
    } else {
      this.searchResultsSubject.next(unfilteredResults);
    }
  }

  private retriggerSearchIfNeeded(): void {
    // Check if a search has already been performed (results exist or search was triggered)
    const hasSearchResults = this.searchResultsSubject.value.length > 0;
    const isSearchInProgress = this.isLoadingResultsSubject.value;
    const wasSearchTriggered = this.isFirstSearchResReturnedSubject.value;

    // If search is in progress, cancel it first
    if (isSearchInProgress) {
      this.cancel();
    }

    // Retrigger search if there were results or a search was already performed
    if (hasSearchResults || wasSearchTriggered) {
      this.search().subscribe();
    }
  }

  /**
   * Reprocess existing shapes when language changes to update layer names
   */
  private reprocessShapesForLanguageChange(): void {
    const currentEntities = this.searchResultsSubject.value;
    if (currentEntities.length > 0) {
      const reprocessedShapes = this.processShapes(currentEntities);
      this.shapesSubject.next(reprocessedShapes);
    }
  }
}
